import React from 'react';
import { TrendingUp, TrendingDown, ArrowRight } from 'lucide-react';
import './StatCardGrid.css';

/**
 * StatCardGrid
 * 
 * A mobile-optimized responsive metric grid & snap-carousel container.
 * Features:
 * - 2 columns on mobile (grid-cols-2), 3 on tablet (sm:grid-cols-3), 6 on desktop (lg:grid-cols-6)
 * - Optional horizontally scrollable snap-carousel for compact mobile viewports
 * - Glassmorphism card styling with icon color variants
 * - Skeleton loading state and micro-interactions
 */
export default function StatCardGrid({
  stats = [],
  layoutMode = 'grid', // 'grid' | 'carousel'
  loading = false,
  onCardClick = null,
  className = ''
}) {
  if (loading) {
    return (
      <div className={`stat-grid-container ${layoutMode} ${className}`}>
        {Array.from({ length: 6 }).map((_, idx) => (
          <div key={idx} className="stat-card-skeleton">
            <div className="skeleton-icon" />
            <div className="skeleton-label" />
            <div className="skeleton-value" />
          </div>
        ))}
      </div>
    );
  }

  if (!stats || stats.length === 0) {
    return null;
  }

  return (
    <div className={`stat-grid-container ${layoutMode === 'carousel' ? 'stat-carousel-layout' : 'stat-grid-layout'} ${className}`}>
      {stats.map((item, index) => {
        const colorVariant = item.color || getColorClassByIndex(index);
        const Icon = item.icon;

        return (
          <article
            key={item.id || item.label || index}
            className={`stat-card-item color-${colorVariant} ${item.onClick || onCardClick ? 'clickable' : ''}`}
            onClick={() => {
              if (item.onClick) item.onClick(item);
              else if (onCardClick) onCardClick(item);
            }}
            tabIndex={item.onClick || onCardClick ? 0 : undefined}
            role={item.onClick || onCardClick ? 'button' : undefined}
          >
            <div className="stat-card-top">
              <span className={`stat-card-icon-badge bg-${colorVariant}`}>
                {React.isValidElement(Icon) ? (
                  Icon
                ) : typeof Icon === 'function' ? (
                  <Icon className="w-5 h-5" />
                ) : (
                  <span className="stat-icon-fallback">•</span>
                )}
              </span>

              {item.change !== undefined && item.change !== null && (
                <span className={`stat-change-pill ${item.changeType === 'down' ? 'down' : 'up'}`}>
                  {item.changeType === 'down' ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
                  {item.change}
                </span>
              )}
            </div>

            <div className="stat-card-body">
              <p className="stat-card-label">{item.label}</p>
              <h3 className="stat-card-value">{item.value}</h3>
              {item.subtitle && <p className="stat-card-subtitle">{item.subtitle}</p>}
            </div>

            {(item.onClick || onCardClick) && (
              <div className="stat-card-footer">
                <span>View Details</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </div>
            )}
          </article>
        );
      })}
    </div>
  );
}

function getColorClassByIndex(index) {
  const colors = ['blue', 'indigo', 'purple', 'emerald', 'amber', 'rose'];
  return colors[index % colors.length];
}
